import { getErrorMessage } from '@/lib/utils/error-utils'

/**
 * 一時障害を表す HTTP ステータス。時間をおけば回復する見込みがあるものだけを入れる。
 *
 * 500 は含めない（アプリケーションのバグであり、再送しても同じ結果になるうえ
 * 副作用だけが積み上がる）。503/502/504 は上流・基盤側の一過性の失敗、
 * 408/429 はサーバーが明示的に「後で来い」と言っている状態。
 */
export const TRANSIENT_HTTP_STATUSES: ReadonlySet<number> = new Set([408, 429, 502, 503, 504])

/** ネットワーク層の失敗（HTTP ステータスを持たない）。status は 0 になる。 */
export const TRANSIENT_NETWORK_CODES: ReadonlySet<string> = new Set([
  'TIMEOUT',
  'NETWORK_ERROR',
  'OFFLINE',
])

export class ApiError extends Error {
  status: number
  code: string
  /** サーバーが Retry-After で指示した待機時間（ミリ秒）。不正値・過去日時のときは undefined。 */
  retryAfterMs?: number
  /** サーバーが発行した相関 ID。障害時にユーザーへ提示し、ログ突き合わせに使う。 */
  requestId?: string
  /** エラー応答の本文そのまま（`logId` など、呼び出し側が個別に読む項目用）。本文が無ければ undefined。 */
  data?: Record<string, unknown>

  constructor(
    message: string,
    status: number,
    code: string,
    meta?: { retryAfterMs?: number; requestId?: string; data?: Record<string, unknown> }
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.retryAfterMs = meta?.retryAfterMs
    this.requestId = meta?.requestId
    this.data = meta?.data
  }

  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500
  }

  get isAuthError(): boolean {
    return this.status === 401 || this.code === 'AUTH_REQUIRED' || this.code === 'AUTH_ERROR'
  }

  get isServerError(): boolean {
    return this.status >= 500
  }

  /**
   * 時間をおけば回復しうる失敗か。リトライ可否の判定はここを唯一の正本にする
   * （機能ごとに独自のステータス一覧を持つと、reception だけが 503 を再試行していて
   * 共通基盤は再試行しない、という食い違いが再発する）。
   *
   * 呼び出し側は必ず isAuthError を先に評価すること。認証切れを再試行しても回復しないため。
   */
  get isTransient(): boolean {
    if (this.code === 'ABORTED') return false
    if (TRANSIENT_HTTP_STATUSES.has(this.status)) return true
    return this.status === 0 && TRANSIENT_NETWORK_CODES.has(this.code)
  }
}

// withAuth の内部タイムアウト (20s) より長く設定する。
// 15s では withDbRetry のリトライ中に先に切れて「タイムアウト」エラーになる (Issue: お客様管理２開けない)。
const REQUEST_TIMEOUT_MS = 25_000

/** 冪等なメソッドだけが自動再送を許される。それ以外は二重登録を招く。 */
const IDEMPOTENT_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD'])

export interface FetchApiRetryOptions {
  /** 初回を除く再試行の上限回数。 */
  maxRetries?: number
  /** バックオフの基準値（ミリ秒）。実際の待機は full jitter で [0, 基準値×2^n] から抽選する。 */
  baseDelayMs?: number
  /** バックオフ上限（ミリ秒）。 */
  capDelayMs?: number
  /**
   * 1 回あたりの待機がこれを超えるなら、自力で待たずに呼び出し側へ返す（ミリ秒）。
   *
   * サーバーが Retry-After で長い待機を指示してきたとき、律儀に待つと画面が何十秒も
   * 固まったまま利用者に何も伝わらない。長い待機は「あと N 秒で再試行できます」と
   * 画面に出して利用者に委ねる方が親切で、こちらが勝手に接続を掴み続けることもない。
   */
  maxDelayMs?: number
}

export interface FetchApiOptions extends RequestInit {
  /**
   * 一時障害の自動再試行。**既定は無効**。
   *
   * 既定を無効にしているのは、react-query の queryFn から fetchApi を呼ぶ構成があるためで、
   * 既定で有効にすると「fetchApi 内の再試行 × react-query の再試行」で試行回数が積算され、
   * 接続が枯渇している最中に負荷を掛け直すことになる。
   *
   * GET / HEAD でのみ指定できる。非冪等メソッドで指定すると即座に例外になる。
   */
  retry?: FetchApiRetryOptions
}

/**
 * Retry-After ヘッダを解釈する。RFC 9110 の delta-seconds と HTTP-date の両形式に対応する。
 * 解釈できない値・過去の日時・負数は「指示なし」として扱い、通常のバックオフに委ねる。
 */
function parseRetryAfter(raw: string | null | undefined): number | undefined {
  if (!raw) return undefined
  const value = raw.trim()
  if (value === '' || value.startsWith('-')) return undefined

  if (/^\d+$/.test(value)) {
    const seconds = Number(value)
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : undefined
  }

  const at = Date.parse(value)
  if (Number.isNaN(at)) return undefined
  const delta = at - Date.now()
  return delta > 0 ? delta : undefined
}

/**
 * サーバーが文言を返さなかったときの表示文言。
 *
 * 汎用の「予期しないエラーが発生しました」に丸めない。それだと利用者も受け手も
 * 「一時的に混んでいるのか」「操作が誤っているのか」を区別できず、
 * 問い合わせのたびに発生時刻と画面を聞き直すことになる。
 */
function fallbackMessage(status: number): string {
  if (status >= 500) return `サーバーが一時的に応答できませんでした（HTTP ${status}）`
  return `リクエストが受け付けられませんでした（HTTP ${status}）`
}

function readHeader(res: Response, name: string): string | undefined {
  return res.headers?.get?.(name) ?? undefined
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function backoffDelay(error: ApiError, attempt: number, options: FetchApiRetryOptions): number {
  if (typeof error.retryAfterMs === 'number' && error.retryAfterMs > 0) {
    return error.retryAfterMs
  }
  const base = options.baseDelayMs ?? 500
  const cap = options.capDelayMs ?? 8_000
  // full jitter: 同時に被災した多数のクライアントが同じ瞬間に再送して山を作るのを防ぐ
  return Math.random() * Math.min(base * 2 ** attempt, cap)
}

async function fetchOnce<T>(url: string, options: FetchApiOptions | undefined): Promise<T> {
  const externalSignal = options?.signal ?? undefined
  const controller = new AbortController()
  const onExternalAbort = () => controller.abort()

  // 呼び出し側の signal をそのまま fetch に渡すと内部タイムアウトが働かなくなるため、
  // 内部 controller に集約し、外部 abort はイベントで転送する。
  externalSignal?.addEventListener('abort', onExternalAbort)
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', ...options?.headers },
      ...options,
      signal: controller.signal,
    })

    if (!res.ok) {
      const body = await res.json().catch(() => ({}) as Record<string, unknown>)
      const requestId =
        readHeader(res, 'X-Request-ID') ??
        (typeof body.requestId === 'string' ? body.requestId : undefined)

      throw new ApiError(
        (body.error as string) || (body.message as string) || fallbackMessage(res.status),
        res.status,
        (body.code as string) || `HTTP_${res.status}`,
        {
          retryAfterMs: parseRetryAfter(readHeader(res, 'Retry-After')),
          requestId,
          data: Object.keys(body).length > 0 ? body : undefined,
        }
      )
    }

    // 204/205 は本文が無い契約（DELETE系等）。res.json() を呼ぶと空本文のパースで
    // 例外になるため、その手前で打ち切る（Codex SO v3 MEDIUM-2）。
    if (res.status === 204 || res.status === 205) {
      return undefined as T
    }

    const json = await res.json()
    return json.data ?? json
  } catch (error) {
    if (error instanceof ApiError) throw error

    if (error instanceof DOMException && error.name === 'AbortError') {
      // 中断の出所で意味が変わる。呼び出し側が中断したなら利用者に見せるべき失敗ではない。
      if (externalSignal?.aborted) {
        throw new ApiError('リクエストが中断されました。', 0, 'ABORTED')
      }
      throw new ApiError('リクエストがタイムアウトしました。', 0, 'TIMEOUT')
    }

    throw new ApiError(getErrorMessage(error), 0, 'NETWORK_ERROR')
  } finally {
    clearTimeout(timeoutId)
    externalSignal?.removeEventListener('abort', onExternalAbort)
  }
}

export async function fetchApi<T>(url: string, options?: FetchApiOptions): Promise<T> {
  if (options?.retry) {
    const method = (options.method ?? 'GET').toUpperCase()
    if (!IDEMPOTENT_METHODS.has(method)) {
      throw new Error(
        `fetchApi: retry は冪等な読み取り (GET / HEAD) でのみ使用できます。` +
          `${method} で自動再送すると、サーバー側が処理を継続している場合に二重登録になります。`
      )
    }
  }

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new ApiError(getErrorMessage(new Error('Failed to fetch')), 0, 'OFFLINE')
  }

  const retry = options?.retry
  const maxRetries = retry?.maxRetries ?? 0

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchOnce<T>(url, options)
    } catch (error) {
      if (attempt === maxRetries) throw error
      if (!(error instanceof ApiError)) throw error
      // 認証切れは再試行しても回復しない。中断は利用者の意思なので再開しない。
      if (error.isAuthError || !error.isTransient) throw error

      const delay = backoffDelay(error, attempt, retry ?? {})
      // 長すぎる待機は自分で抱えず、待機指示ごと呼び出し側へ返して画面に出させる
      if (typeof retry?.maxDelayMs === 'number' && delay > retry.maxDelayMs) throw error

      await sleep(delay)
    }
  }

  // ループは必ず return か throw で終わる（maxRetries >= 0 のため最低 1 回は試行する）。
  throw new ApiError('リクエストを実行できませんでした。', 0, 'NETWORK_ERROR')
}
